/**
 * 画布指针模式：选择 vs 小手平移（等同空格 + 左键拖拽）
 */
import { useCallback, useEffect, useState } from 'react';

function isEditingInput(): boolean {
  const active = document.activeElement;
  return (
    active?.getAttribute('contenteditable') === 'true' ||
    active?.tagName === 'INPUT' ||
    active?.tagName === 'TEXTAREA'
  );
}

export interface UseCanvasPointerModeOptions {
  /** 空格松开时回调（如结束视口拖拽） */
  onSpaceRelease?: () => void;
  /** 为 false 时不注册空格平移（演示全屏时由播放快捷键占用 Space） */
  spacePanEnabled?: boolean;
}

export function useCanvasPointerMode(options: UseCanvasPointerModeOptions = {}) {
  const { onSpaceRelease, spacePanEnabled = true } = options;
  const [handPanToolActive, setHandPanToolActive] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);

  const isPanModeActive = handPanToolActive || isSpacePressed;
  const isHandUiActive = isPanModeActive;

  const selectPointerMode = useCallback(() => setHandPanToolActive(false), []);
  const selectHandMode = useCallback(() => setHandPanToolActive(true), []);

  const onSpaceDown = useCallback(() => {
    setIsSpacePressed(true);
  }, []);

  const onSpaceUp = useCallback(() => {
    setIsSpacePressed(false);
    onSpaceRelease?.();
  }, [onSpaceRelease]);

  useEffect(() => {
    if (!spacePanEnabled) {
      setIsSpacePressed(false);
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat || isEditingInput()) return;
      e.preventDefault();
      onSpaceDown();
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      onSpaceUp();
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [spacePanEnabled, onSpaceDown, onSpaceUp]);

  return {
    handPanToolActive,
    isSpacePressed,
    isPanModeActive,
    isHandUiActive,
    selectPointerMode,
    selectHandMode,
    onSpaceDown,
    onSpaceUp,
  };
}
